import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { addSeconds, differenceInMilliseconds } from 'date-fns';
import { useLocalStorage } from 'usehooks-ts';

import { PopupNames } from 'contexts/PopupContext';

import { UserTransferApi } from 'api/UserTransferApi';
import { isMonetizationPage } from 'helpers/route';
import { getIsBuyer, getIsInterstitial } from 'store/auth/selectors';
import { getIsCommonStateLoading } from 'store/common/selectors';

import { usePopup } from './usePopup';

export const IS_QUALIFICATION_SET_STORAGE_KEY = 'IS_QUALIFICATION_SET';
const NEXT_INTERSTITIAL_POPUP_STORAGE_KEY = 'NEXT_INTERSTITIAL_POPUP';
const INTERSTITIAL_TIMERS_DELAY = 180;
const USER_ALREADY_QUALIFIED_STATUS = 400;

export const useInterstitialPopup = () => {
  const isLoading = useSelector(getIsCommonStateLoading);
  const isInterstitialActive = useSelector(getIsInterstitial);
  const isBuyer = useSelector(getIsBuyer);

  const { pathname } = useLocation();

  const timer = useRef<NodeJS.Timeout | null>(null);

  const [isQualification, setIsQualification] = useLocalStorage<number | null>(
    IS_QUALIFICATION_SET_STORAGE_KEY,
    null
  );
  const [nextInterstitialTime, setNextInterstitialTime] = useLocalStorage<
    string | null
  >(NEXT_INTERSTITIAL_POPUP_STORAGE_KEY, null);

  const {
    handleOpenASAP,
    handleAction,
    handleClose,
    handleRemoveFromQueue,
    checkIsInQueue,
  } = usePopup(PopupNames.Interstitial);

  handleAction.current = (action: { type: string }) => {
    if (action.type === 'close') handleClose();
  };

  const retryFetchQualification = useCallback(
    async (attemptsLeft = 3) => {
      try {
        await UserTransferApi.fetchQualification();
        setIsQualification(1);
      } catch (error: any) {
        if (error?.response?.status === USER_ALREADY_QUALIFIED_STATUS) {
          setIsQualification(1);

          return;
        }

        if (attemptsLeft > 1) {
          await retryFetchQualification(attemptsLeft - 1);
        }
      }
    },
    [setIsQualification]
  );

  const handleOpenPopup = useCallback(async () => {
    if (!isQualification) {
      await retryFetchQualification();
    }

    setNextInterstitialTime(
      String(addSeconds(new Date(), INTERSTITIAL_TIMERS_DELAY))
    );

    if (!checkIsInQueue()) {
      handleOpenASAP(null);
    }

    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, [
    checkIsInQueue,
    handleOpenASAP,
    isQualification,
    retryFetchQualification,
    setNextInterstitialTime,
  ]);

  // show interstitial if storage key set
  useEffect(() => {
    if (!isInterstitialActive || !nextInterstitialTime || isBuyer)
      return undefined;

    timer.current = setTimeout(() => {
      handleOpenPopup();
    }, differenceInMilliseconds(new Date(nextInterstitialTime), new Date()));

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [handleOpenPopup, isBuyer, isInterstitialActive, nextInterstitialTime]);

  // set key
  useEffect(() => {
    const shouldSetPopupTime =
      !isMonetizationPage(pathname) &&
      !nextInterstitialTime &&
      isInterstitialActive &&
      !isBuyer;

    if (shouldSetPopupTime) {
      setNextInterstitialTime(
        String(addSeconds(new Date(), INTERSTITIAL_TIMERS_DELAY))
      );
    }
  }, [
    isBuyer,
    isInterstitialActive,
    pathname,
    setNextInterstitialTime,
    nextInterstitialTime,
  ]);

  // reset
  useEffect(() => {
    const shouldResetPopup =
      nextInterstitialTime && !isLoading && (!isInterstitialActive || isBuyer);

    if (shouldResetPopup) {
      setNextInterstitialTime(null);
      setIsQualification(null);
      handleRemoveFromQueue();
      handleClose();

      if (timer.current) {
        clearTimeout(timer.current);
      }
    }
  }, [
    handleClose,
    handleRemoveFromQueue,
    isBuyer,
    isInterstitialActive,
    isLoading,
    nextInterstitialTime,
    setIsQualification,
    setNextInterstitialTime,
  ]);
};
